import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './DistDiscountsConfig.css';
import './DistPendingConversionsPage.css';

const STALE_HOURS = 24;

export default function DistPendingConversionsPage() {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [busyId,  setBusyId]  = useState(null);
  const [convertForm, setConvertForm] = useState({}); // id -> {order_name, amount}

  function reload() {
    setLoading(true);
    axios.get('/api/dist-orders/pending-conversion')
      .then(r => { setRecords(r.data || []); setLoading(false); })
      .catch(err => {
        const status = err.response?.status;
        setError(status === 401
          ? 'Failed to load — you may have been signed out. Try refreshing the page.'
          : `Failed to load pending conversions${status ? ` (${status})` : ''}`);
        setLoading(false);
      });
  }

  useEffect(() => { reload(); }, []);

  function checkNow() {
    setChecking(true);
    setCheckResult(null);
    axios.post('/api/dist-orders/pending-conversion/check-now')
      .then(r => { setCheckResult(r.data); setChecking(false); reload(); })
      .catch(() => { setError('Check failed'); setChecking(false); });
  }

  function patchRecord(id, body) {
    setBusyId(id);
    return axios.patch(`/api/dist-orders/pending-conversion/${id}`, body)
      .then(() => { setBusyId(null); reload(); })
      .catch(() => { setError('Update failed'); setBusyId(null); });
  }

  function saveEmail(id, email, prev) {
    if (email === prev) return;
    patchRecord(id, { created_by_email: email });
  }

  function deleteDraft(id) {
    if (!window.confirm(
      'Delete this draft order? This deletes it in Shopify and removes its row from the PO sheet. ' +
      "A snapshot of the row is kept in this tool's log in case it needs to be restored by hand."
    )) return;
    setBusyId(id);
    axios.post(`/api/dist-orders/pending-conversion/${id}/delete`)
      .then(r => {
        setBusyId(null);
        if (r.data?.sheet_error) setError(`Draft deleted in Shopify, but the sheet row could not be removed: ${r.data.sheet_error}`);
        else if (r.data?.sheet_row_deleted === false) setError('Draft deleted in Shopify, but no matching sheet row was found to remove.');
        reload();
      })
      .catch(err => { setError(err.response?.data?.error || 'Delete failed'); setBusyId(null); });
  }

  function reopen(id) {
    patchRecord(id, { conversion_status: 'pending' });
  }

  function submitConvert(id) {
    const f = convertForm[id] || {};
    if (!f.order_name || !f.amount) return;
    patchRecord(id, {
      conversion_status: 'converted',
      converted_order_name: f.order_name,
      converted_amount: f.amount,
    }).then(() => setConvertForm(prev => ({ ...prev, [id]: undefined })));
  }

  return (
    <div className="ddcfg-page">
      <div className="ddcfg-header">
        <button className="ddcfg-back" onClick={() => navigate('/draft-orders')}>← Back</button>
        <div>
          <h1>Pending Conversions</h1>
          <p className="ddcfg-muted">
            Every created draft order is tracked here until it either converts to a real Shopify order (the PO
            sheet row is corrected to the real order # and final amount) or gets deleted (tracking just stops).
            Checked automatically every 30 minutes; if a draft hasn't been updated in {STALE_HOURS}h, the person
            who ran that batch gets an email. You can also edit an entry by hand below.
          </p>
        </div>
      </div>

      {error && (
        <div className="ddcfg-error">{error} <button onClick={() => setError(null)}>✕</button></div>
      )}

      <div className="ddcfg-section pcv-actions">
        <button className="ddcfg-btn-primary" onClick={checkNow} disabled={checking}>
          {checking ? 'Checking…' : 'Check now'}
        </button>
        {checkResult && (
          <span className="ddcfg-muted-sm pcv-result">
            Checked {checkResult.checked} · {checkResult.converted} converted · {checkResult.deleted} deleted · {checkResult.stale_emailed} stale email(s) sent
          </span>
        )}
      </div>

      {loading ? (
        <p className="ddcfg-muted">Loading…</p>
      ) : records.length === 0 ? (
        <p className="ddcfg-muted">No draft orders tracked yet.</p>
      ) : (
        <div className="ddcfg-table-wrap">
          <table className="ddcfg-table">
            <thead>
              <tr>
                <th>PO</th>
                <th>Customer</th>
                <th>Draft / Order</th>
                <th>Amount</th>
                <th>Created by</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => {
                const isStale = r.conversion_status === 'pending' && r.stale_hours != null && r.stale_hours >= STALE_HOURS;
                const form = convertForm[r.id] || {};
                return (
                  <React.Fragment key={r.id}>
                    <tr>
                      <td>{r.po_number}</td>
                      <td>{r.customer_name || '—'}</td>
                      <td>
                        {r.conversion_status === 'converted' ? (
                          r.converted_order_url ? (
                            <a className="pcv-link" href={r.converted_order_url} target="_blank" rel="noreferrer">
                              {r.converted_order_name}
                            </a>
                          ) : (r.converted_order_name || '—')
                        ) : (
                          r.draft_order?.url ? (
                            <a className="pcv-link" href={r.draft_order.url} target="_blank" rel="noreferrer">
                              {r.draft_order.name}
                            </a>
                          ) : (r.draft_order?.name || '—')
                        )}
                      </td>
                      <td>
                        {r.conversion_status === 'converted'
                          ? (r.converted_amount != null ? `$${Number(r.converted_amount).toFixed(2)}` : '—')
                          : (r.amount != null ? `$${Number(r.amount).toFixed(2)}` : '—')}
                      </td>
                      <td>
                        <input
                          className="ddcfg-input pcv-email-input"
                          defaultValue={r.created_by_email || ''}
                          placeholder="email"
                          disabled={busyId === r.id}
                          onBlur={e => saveEmail(r.id, e.target.value.trim() || null, r.created_by_email)}
                        />
                      </td>
                      <td>
                        <span className={`pcv-badge pcv-badge--${r.conversion_status}`}>{r.conversion_status}</span>
                        {isStale && <span className="pcv-badge pcv-badge--stale">stale {r.stale_hours}h</span>}
                        {r.manual_corrections && r.manual_corrections.length > 0 && (
                          <span
                            className="pcv-badge pcv-badge--corrected"
                            title={r.manual_corrections.map(c =>
                              c.field === 'sku'
                                ? `SKU corrected: ${c.original} → ${c.corrected} (${c.description || ''})`
                                : `Quantity corrected: ${c.original} → ${c.corrected} (${c.sku || ''})`
                            ).join('\n')}
                          >
                            ✎ {r.manual_corrections.length} corrected
                          </span>
                        )}
                      </td>
                      <td className="pcv-row-actions">
                        {r.conversion_status === 'pending' && (
                          <>
                            <button
                              className="ddcfg-link-btn"
                              disabled={busyId === r.id}
                              onClick={() => setConvertForm(prev => ({ ...prev, [r.id]: prev[r.id] ? undefined : { order_name: '', amount: '' } }))}
                            >
                              Mark converted…
                            </button>
                            <button className="ddcfg-remove" disabled={busyId === r.id} onClick={() => deleteDraft(r.id)}>
                              Delete draft
                            </button>
                          </>
                        )}
                        {r.conversion_status !== 'pending' && (
                          <button className="ddcfg-link-btn" disabled={busyId === r.id} onClick={() => reopen(r.id)}>
                            Reopen
                          </button>
                        )}
                      </td>
                    </tr>
                    {convertForm[r.id] && (
                      <tr className="ddcfg-expand-row">
                        <td colSpan={7}>
                          <div className="ddcfg-sku-panel pcv-convert-panel">
                            <input
                              className="ddcfg-input"
                              placeholder="Real order # (e.g. 1489)"
                              value={form.order_name || ''}
                              onChange={e => setConvertForm(prev => ({ ...prev, [r.id]: { ...prev[r.id], order_name: e.target.value } }))}
                            />
                            <input
                              className="ddcfg-input"
                              placeholder="Final amount"
                              type="number"
                              step="0.01"
                              value={form.amount || ''}
                              onChange={e => setConvertForm(prev => ({ ...prev, [r.id]: { ...prev[r.id], amount: e.target.value } }))}
                            />
                            <button
                              className="ddcfg-btn-primary"
                              disabled={!form.order_name || !form.amount || busyId === r.id}
                              onClick={() => submitConvert(r.id)}
                            >
                              Save
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
